import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

const STATUS_LABELS = {
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  unknown: 'Unknown',
};

const STATUS_COLORS = {
  in_transit: '#818cf8',
  out_for_delivery: '#fbbf24',
  delivered: '#34d399',
  unknown: '#71717a',
};

const CARRIER_ICONS = {
  Amazon: '\uD83D\uDCE6',
  UPS: '\uD83D\uDFEB',
  FedEx: '\uD83D\uDFE3',
  USPS: '\uD83D\uDFE6',
  Unknown: '\uD83D\uDCEB',
};

function PackageTracking({ refreshKey }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [activeFilter, setActiveFilter] = useState('active');

  const fetchPackages = useCallback(async () => {
    try {
      const res = await apiFetch('/api/packages');
      if (res.ok) setPackages(await res.json());
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [refreshKey, fetchPackages]);

  async function scanEmails() {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await apiFetch('/api/packages/scan', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setScanResult(data.result);
        await fetchPackages();
      }
    } catch (err) {
      console.error('Failed to scan:', err);
    } finally {
      setScanning(false);
    }
  }

  async function markDelivered(id) {
    setPackages((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'delivered' } : p)));
    try {
      await apiFetch(`/api/packages/${id}/delivered`, { method: 'POST' });
    } catch {
      await fetchPackages();
    }
  }

  async function deletePackage(id) {
    setPackages((prev) => prev.filter((p) => p.id !== id));
    try {
      await apiFetch(`/api/packages/${id}`, { method: 'DELETE' });
    } catch {
      await fetchPackages();
    }
  }

  const filtered = packages.filter((p) => {
    if (activeFilter === 'active') return p.status !== 'delivered';
    if (activeFilter === 'delivered') return p.status === 'delivered';
    return true;
  });

  const activeCounts = {
    active: packages.filter((p) => p.status !== 'delivered').length,
    delivered: packages.filter((p) => p.status === 'delivered').length,
    all: packages.length,
  };

  if (loading) {
    return (
      <section className="card">
        <h2>Package Tracking</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  return (
    <section className="card pkg-section">
      <div className="goals-header">
        <h2>Package Tracking</h2>
        <button className="manage-btn" onClick={scanEmails} disabled={scanning}>
          {scanning ? 'Scanning...' : 'Scan Emails'}
        </button>
      </div>

      {scanResult && (
        <div className="pkg-scan-result">
          <span>{scanResult.found} new</span>
          <span>{scanResult.skipped} skipped</span>
          <span>{scanResult.total} total</span>
        </div>
      )}

      <div className="pkg-tabs">
        {[
          { key: 'active', label: 'Active' },
          { key: 'delivered', label: 'Delivered' },
          { key: 'all', label: 'All' },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`pkg-tab${activeFilter === tab.key ? ' active' : ''}`}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label} ({activeCounts[tab.key]})
          </button>
        ))}
      </div>

      <div className="pkg-list">
        {filtered.length === 0 && (
          <div className="pkg-empty">
            {activeFilter === 'active' ? 'No active packages' : 'No packages found'}
          </div>
        )}
        {filtered.map((pkg) => (
          <div key={pkg.id} className="pkg-item">
            <div className="pkg-carrier-icon">
              {CARRIER_ICONS[pkg.carrier] || CARRIER_ICONS.Unknown}
            </div>
            <div className="pkg-info">
              <div className="pkg-desc">{pkg.description}</div>
              <div className="pkg-meta">
                <span className="pkg-vendor">{pkg.vendor}</span>
                <span className="pkg-carrier-name">{pkg.carrier}</span>
                {pkg.trackingNumber && (
                  <span className="pkg-tracking">
                    {pkg.trackingUrl ? (
                      <a href={pkg.trackingUrl} target="_blank" rel="noopener noreferrer" className="pkg-track-link">
                        {pkg.trackingNumber.length > 20
                          ? pkg.trackingNumber.slice(0, 20) + '...'
                          : pkg.trackingNumber}
                      </a>
                    ) : (
                      pkg.trackingNumber
                    )}
                  </span>
                )}
              </div>
              <div className="pkg-meta">
                {pkg.detectedDate && (
                  <span className="pkg-date">
                    Detected: {new Date(pkg.detectedDate).toLocaleDateString('en-US', {
                      timeZone: 'America/Chicago',
                      month: 'short', day: 'numeric',
                    })}
                  </span>
                )}
                {pkg.estimatedDelivery && (
                  <span className="pkg-eta">ETA: {pkg.estimatedDelivery}</span>
                )}
              </div>
            </div>
            <div className="pkg-right">
              <span
                className="pkg-status-badge"
                style={{ background: STATUS_COLORS[pkg.status] || '#71717a' }}
              >
                {STATUS_LABELS[pkg.status] || pkg.status}
              </span>
              <div className="pkg-actions">
                {pkg.status !== 'delivered' && (
                  <button className="pkg-action-btn pkg-deliver-btn" onClick={() => markDelivered(pkg.id)}>
                    Delivered
                  </button>
                )}
                <button className="pkg-action-btn pkg-delete-btn" onClick={() => deletePackage(pkg.id)}>
                  &times;
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default PackageTracking;
